import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";

import expenseCategoriesApi from "@/services/api/expenseCategoriesApi";

export const useExpenseCategoryStore = defineStore(
  "expenseCategory",
  {
    state: () => ({
      expenseCategories: [],

      companyId: getCompanyId(),

      searchQuery: "",

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },

      dataIsLoading: false,

      selectItems: [],
      selectSearchQuery: "",
      selectPagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },
      selectIsLoading: false,
    }),

    getters: {
      // Опции для BaseStatusSelect: категории расходов с emoji в label и нейтральным фоном
      expenseCategorySelectOptions: (state) =>
        state.expenseCategories.map((cat) => ({
          value: String(cat.id),
          label: `${cat.emoji} ${cat.name}`,
        })),
    },

    actions: {
      async fetchExpenseCategories(payload = {}) {
        this.searchQuery = payload.search || "";
        return await this.getExpenseCategoriesRequest(1);
      },

      async fetchSelectItems(payload = {}) {
        this.selectSearchQuery = payload.search || "";
        return await this.getSelectItemsRequest(1);
      },

      async loadMoreExpenseCategories() {
        if (this.dataIsLoading || !this.pagination.next) {
          return;
        }

        const nextPage = this.pagination.page + 1;
        return await this.getExpenseCategoriesRequest(nextPage);
      },

      async getExpenseCategoriesRequest(page) {
        const pageNumber = parseInt(page) || 1;

        try {
          this.dataIsLoading = true;

          const params = { page: pageNumber };
          params["q[enabled]"] = "true";

          if (this.searchQuery) {
            params["q[search]"] = this.searchQuery;
          }

          const response = await expenseCategoriesApi.index(
            this.companyId,
            params
          );

          if (pageNumber === 1) {
            this.expenseCategories = response.data.result;
          } else {
            this.expenseCategories = [
              ...this.expenseCategories,
              ...response.data.result,
            ];
          }

          const meta = response.data.pagination_meta;
          this.pagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series,
            next: meta.next,
            prev: meta.prev,
            count: meta.count,
            limit: meta.limit,
          };
        } catch (error) {
          console.error(
            "Ошибка при загрузке категорий расходов:",
            error
          );
        } finally {
          this.dataIsLoading = false;
        }
      },

      async loadMoreSelectItems() {
        if (this.selectIsLoading || !this.selectPagination.next) {
          return;
        }

        const nextPage = this.selectPagination.page + 1;
        return await this.getSelectItemsRequest(nextPage);
      },

      async getSelectItemsRequest(page) {
        const pageNumber = parseInt(page) || 1;

        try {
          this.selectIsLoading = true;

          const params = { page: pageNumber };
          params["q[enabled]"] = "true";

          if (this.selectSearchQuery) {
            params["q[search]"] = this.selectSearchQuery;
          }

          const response = await expenseCategoriesApi.index(
            this.companyId,
            params
          );

          if (pageNumber === 1) {
            this.selectItems = response.data.result;
          } else {
            this.selectItems = [
              ...this.selectItems,
              ...response.data.result,
            ];
          }

          const meta = response.data.pagination_meta;
          this.selectPagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series,
            next: meta.next,
            prev: meta.prev,
            count: meta.count,
            limit: meta.limit,
          };
        } catch (error) {
          console.error(
            "Ошибка при загрузке категорий расходов для селекта:",
            error
          );
        } finally {
          this.selectIsLoading = false;
        }
      },

      async fetchSelectItem(categoryId) {
        const exists =
          this.selectItems.find(
            (category) => String(category.id) === String(categoryId)
          ) ||
          this.expenseCategories.find(
            (category) => String(category.id) === String(categoryId)
          );
        if (exists) return exists;

        const response = await expenseCategoriesApi.show(
          this.companyId,
          categoryId
        );

        return response.data.result || response.data;
      },

      async createExpenseCategory(payload) {
        try {
          const response = await expenseCategoriesApi.create(
            this.companyId,
            payload
          );
          const created = response.data.result || response.data;
          this.clearExpenseCategories();
          return created;
        } catch (error) {
          console.error(
            "Ошибка при создании категории расходов:",
            error
          );
          throw error;
        }
      },

      async updateExpenseCategory(categoryId, payload) {
        try {
          const response = await expenseCategoriesApi.update(
            this.companyId,
            categoryId,
            payload
          );
          const updated = response.data.result || response.data;
          this.clearExpenseCategories();
          return updated;
        } catch (error) {
          console.error(
            "Ошибка при обновлении категории расходов:",
            error
          );
          throw error;
        }
      },

      async toggleExpenseCategory(categoryId) {
        try {
          const response = await expenseCategoriesApi.toggle(
            this.companyId,
            categoryId
          );
          const updated = response.data.result || response.data;
          this.clearExpenseCategories();
          return updated;
        } catch (error) {
          console.error(
            "Ошибка при переключении категории расходов:",
            error
          );
          throw error;
        }
      },

      clearExpenseCategories() {
        this.expenseCategories = [];
        this.searchQuery = "";
        this.pagination = {
          page: 1,
          pages: 1,
          series: [],
          next: null,
          prev: null,
          count: 0,
          limit: 40,
        };
        this.clearSelectItems();
      },

      clearSelectItems() {
        this.selectItems = [];
        this.selectSearchQuery = "";
        this.selectPagination = {
          page: 1,
          pages: 1,
          series: [],
          next: null,
          prev: null,
          count: 0,
          limit: 40,
        };
      },
    },
  }
);
